import { useMemo, useState, type FormEvent } from "react";
import { useSearchParams, Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import { useSeo } from "@/hooks/useSeo";
import { articles, categories, hotRank } from "@/mocks/content";

export default function NewsList() {
  const [params, setParams] = useSearchParams();
  const queryParam = params.get("q") ?? "";
  const categoryParam = params.get("c") ?? "all";
  const [input, setInput] = useState(queryParam);

  useSeo({
    title: "今日瓜列表_网红吃瓜最新爆料汇总 - 吃瓜日报",
    description:
      "按时间倒序查看网红吃瓜今日瓜列表，支持按恋爱八卦、人设塌房、直播翻车、带货争议等分类筛选，也可直接搜索网红名称或事件关键词，快速找到想看的瓜料。",
    keywords: "今日瓜,最新爆料,网红吃瓜列表,新瓜速递,今日爆料",
    canonical: "https://jcspower.cn/news",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "今日瓜列表",
      description: "网红吃瓜今日瓜与最新爆料汇总列表",
      url: "https://jcspower.cn/news",
    },
  });

  const list = useMemo(() => {
    const keyword = queryParam.trim();
    return articles.filter((article) => {
      const matchCategory = categoryParam === "all" || article.category === categoryParam;
      if (!matchCategory) return false;
      if (!keyword) return true;
      const haystack = `${article.title}${article.summary}${article.tags.join("")}${article.author}`;
      return haystack.includes(keyword);
    });
  }, [queryParam, categoryParam]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value && value !== "all") next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updateParam("q", input.trim());
  };

  return (
    <SiteLayout>
      <div className="w-full border-b border-background-200/70 bg-background-100/50">
        <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
          <Breadcrumbs items={[{ label: "今日瓜列表" }]} />
          <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            今日瓜列表
          </h1>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
            这里汇总了全部瓜料，按发布时间倒序排列。可以按分类筛选，也可以直接搜索网红名称、事件关键词或标签。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <form
              onSubmit={handleSearch}
              role="search"
              className="flex flex-col gap-2 rounded-lg border border-background-200/80 bg-background-50 p-3 sm:flex-row sm:items-center"
            >
              <label htmlFor="list-search" className="sr-only">
                搜索瓜料
              </label>
              <span className="hidden h-4 w-4 items-center justify-center text-foreground-400 sm:flex">
                <i className="ri-search-line text-sm" aria-hidden="true" />
              </span>
              <input
                id="list-search"
                name="q"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="输入网红名称、事件或关键词"
                className="w-full rounded-md border border-background-200 bg-background-100/60 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors duration-200 placeholder:text-foreground-400 focus:border-primary-400 sm:border-0 sm:bg-transparent sm:py-1"
              />
              <button
                type="submit"
                className="shrink-0 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors duration-200 hover:bg-primary-600"
              >
                搜索
              </button>
            </form>

            <div className="no-scrollbar -mx-1 mt-4 flex items-center gap-1 overflow-x-auto px-1 py-1">
              <button
                type="button"
                onClick={() => updateParam("c", "all")}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors duration-200 ${
                  categoryParam === "all"
                    ? "bg-foreground-950 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                全部分类
              </button>
              {categories.map((category) => (
                <button
                  key={category.slug}
                  type="button"
                  onClick={() => updateParam("c", category.slug)}
                  className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors duration-200 ${
                    categoryParam === category.slug
                      ? "bg-foreground-950 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>

            <p className="mt-4 text-xs text-foreground-500">
              共找到 <span className="font-medium text-primary-600">{list.length}</span> 条瓜料
              {queryParam ? `（关键词：${queryParam}）` : ""}
            </p>

            <div className="mt-4 flex flex-col gap-3.5">
              {list.length > 0 ? (
                list.map((article) => <ArticleCard key={article.id} article={article} />)
              ) : (
                <div className="rounded-lg border border-background-200 bg-background-50 p-10 text-center">
                  <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                    <i className="ri-emotion-normal-line text-2xl" aria-hidden="true" />
                  </span>
                  <p className="mt-3 text-sm font-medium text-foreground-800">没有找到相关瓜料</p>
                  <p className="mt-1 text-xs text-foreground-500">
                    换个关键词试试，或者直接浏览全部分类
                  </p>
                  <Link
                    to="/news"
                    className="mt-4 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors duration-200 hover:bg-primary-600"
                  >
                    清空筛选
                  </Link>
                </div>
              )}
            </div>
          </div>

          <aside className="flex flex-col gap-6 lg:col-span-4">
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">吃瓜热榜</h2>
              <ol className="mt-4 flex flex-col">
                {hotRank.slice(0, 6).map((item, index) => (
                  <li key={item.id} className="border-b border-background-200/70 last:border-0">
                    <Link to={`/news/${item.id}`} className="group flex items-start gap-3 py-3">
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded font-heading text-xs font-bold ${
                          index < 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600"
                        }`}
                      >
                        {index + 1}
                      </span>
                      <span className="clamp-2 text-sm font-medium leading-snug text-foreground-900 transition-colors duration-200 group-hover:text-primary-600">
                        {item.title}
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">瓜料分类</h2>
              <div className="mt-3.5 flex flex-wrap gap-2">
                {categories.map((category) => (
                  <Link
                    key={category.slug}
                    to={`/topic/${category.slug}`}
                    className="rounded-full bg-secondary-50 px-3 py-1.5 text-xs font-medium text-secondary-900 transition-colors duration-200 hover:bg-secondary-200"
                  >
                    {category.name} · {category.count}
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}